import { apiClient, assertValidParam } from '@v2/lib';
import type { OfferingPlugin, RemovePluginFromOfferingResponse } from '@v2/types';

/**
 * Plugins Service
 *
 * Domain: Mission Control plugin management for offerings
 * Base paths: /missioncontrol/plugins, /MissionControl/plugins
 *
 * Note: GET endpoints use lowercase "missioncontrol", mutation endpoints use
 * PascalCase "MissionControl". This mirrors the current backend routing.
 *
 * Queries:
 *   - getOfferingPlugins(offeringName)          → GET /missioncontrol/plugins/offering?offering=
 *   - getActiveNonOfferingPlugins(offeringName)  → GET /missioncontrol/plugins/active/nonoffering?offering=
 *
 * Mutations:
 *   - addPluginToOffering(offeringName, pluginId)    → POST /MissionControl/plugins/offering?offeringName=&pluginId=
 *   - removePluginFromOffering(offeringName, pluginId) → DELETE /MissionControl/plugins/offering?offeringName=&pluginId=
 */

export const pluginsService = {
  getOfferingPlugins: async (
    offeringName: string
  ): Promise<OfferingPlugin[]> => {
    const safeName = assertValidParam(offeringName, 'offeringName');
    const response = await apiClient().get<OfferingPlugin[]>(
      'missioncontrol/plugins/offering',
      { params: { offering: safeName } }
    );
    return response.data;
  },

  getActiveNonOfferingPlugins: async (
    offeringName: string
  ): Promise<OfferingPlugin[]> => {
    const safeName = assertValidParam(offeringName, 'offeringName');
    const response = await apiClient().get<OfferingPlugin[]>(
      'missioncontrol/plugins/active/nonoffering',
      { params: { offering: safeName } }
    );
    return response.data;
  },

  addPluginToOffering: async (
    offeringName: string,
    pluginId: string
  ): Promise<string> => {
    const safeName = assertValidParam(offeringName, 'offeringName');
    const safePluginId = assertValidParam(pluginId, 'pluginId');
    const response = await apiClient().post<string>(
      'MissionControl/plugins/offering',
      undefined,
      { params: { offeringName: safeName, pluginId: safePluginId } }
    );
    return typeof response.data === 'string'
      ? response.data
      : JSON.stringify(response.data);
  },

  removePluginFromOffering: async (
    offeringName: string,
    pluginId: string
  ): Promise<RemovePluginFromOfferingResponse | null> => {
    const safeName = assertValidParam(offeringName, 'offeringName');
    const safePluginId = assertValidParam(pluginId, 'pluginId');
    const response =
      await apiClient().delete<RemovePluginFromOfferingResponse>(
        'MissionControl/plugins/offering',
        { params: { offeringName: safeName, pluginId: safePluginId } }
      );
    return response.data ?? null;
  },
};
